import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FiltresService } from '../filtres/filtres.service';
import { environment } from 'src/environments/environment';
import { Filtres } from 'src/app/utils/interfaces/new/filtres.interface';
import { Paginated } from 'src/app/utils/interfaces/new/paginated.interface';
import { ProtocolePluriprofessionnel } from 'src/app/utils/interfaces/new/ProtocolePluriprofessionnel';
import { CreationProtocolePluriprofessionnel } from 'src/app/utils/interfaces/new/CreationProtocolePluriprofessionnel';
import { ReunionPluriprofessionnellePersonnelSoignant } from 'src/app/utils/interfaces/new/ReunionPluriprofessionnellePersonnelSoignant';
import { CreationProtocolePluriprofessionnelPatient } from 'src/app/utils/interfaces/new/CreationProtocolePluriprofessionnelPatient';
import { SnackbarService } from '../snackbar/snackbar.service';

const MULTIPROFESSIONAL_PROTOCOL_PATH = '/user/multiprofessional-protocol/';

@Injectable({
  providedIn: 'root'
})
export class MultiprofessionalProtocolService {

  private httpOptions = {
    withCredentials: true,
    observe: 'response' as 'response',
    headers: new HttpHeaders(),
    params: undefined
  };

  private basePath = environment.basePath + MULTIPROFESSIONAL_PROTOCOL_PATH;

  constructor(
    private _http: HttpClient,
    private _filtresService: FiltresService,
    private _snack: SnackbarService
  ) { }

  public async getAllMultiprofessionnalProtocols(resourceId: string, filtre: Filtres = {
    perpage: -1,
    currentPage: 1,
  }): Promise<Paginated<ProtocolePluriprofessionnel>> {
    return new Promise<Paginated<ProtocolePluriprofessionnel>>(async (resolve, reject) => {
      try {
        this.httpOptions.headers = this._filtresService.setHeaders(filtre);
        const res = await this._http.get(`${this.basePath}resource/${resourceId}`, this.httpOptions).toPromise();
        const protocols = res.body as Array<ProtocolePluriprofessionnel>;
        const paginated: Paginated<ProtocolePluriprofessionnel> = {
          page: Number(res.headers.get('x-page')),
          perpage: Number(res.headers.get('x-per-page')),
          total: Number(res.headers.get('x-total')),
          objects: protocols
        };
        resolve(paginated);
      } catch (e) {
        this._snack.openSnackBar(e.error.message);
        reject(e);
      }
    });
  }

  public async getOneMultiprofessionnalProtocol(protocolId: string): Promise<ProtocolePluriprofessionnel> {
    return new Promise<ProtocolePluriprofessionnel>(async (resolve, reject) => {
      try {
        const res = await this._http.get(`${this.basePath}${protocolId}`, this.httpOptions).toPromise();
        resolve(res.body as ProtocolePluriprofessionnel);
      } catch (e) {
        this._snack.openSnackBar(e.error.message);
        reject(e);
      }
    });
  }

  public async createMultiprofessionnalProtocols(protocol: CreationProtocolePluriprofessionnel): Promise<ProtocolePluriprofessionnel> {
    return new Promise<ProtocolePluriprofessionnel>(async (resolve, reject) => {
      try {
        const updatedProtocol = await this._http.post<ProtocolePluriprofessionnel>(
          `${this.basePath}`,
          protocol,
          this.httpOptions
        ).toPromise();
        resolve(updatedProtocol.body);
      } catch (e) {
        this._snack.openSnackBar(e.error.message);
        reject(e);
      }
    });
  }

  public async updateMultiprofessionnalProtocols(
    protocolId: string,
    protocol: CreationProtocolePluriprofessionnel
  ): Promise<ProtocolePluriprofessionnel> {
    return new Promise<ProtocolePluriprofessionnel>(async (resolve, reject) => {
      try {
        const updatedProtocol = await this._http.put<ProtocolePluriprofessionnel>(
          `${this.basePath}${protocolId}`,
          protocol,
          this.httpOptions
        ).toPromise();
        resolve(updatedProtocol.body);
      } catch (e) {
        this._snack.openSnackBar(e.error.message);
        reject(e);
      }
    });
  }

  public async deleteMultiprofessionnalProtocols(protocolId: string): Promise<void> {
    return new Promise<void>(async (resolve, reject) => {
      try {
        await this._http.delete(`${this.basePath}${protocolId}`, this.httpOptions).toPromise();
        resolve();
      } catch (e) {
        this._snack.openSnackBar(e.error.message);
        reject(e);
      }
    });
  }

  public convertProtocolToCreationProtocol(protocol: ProtocolePluriprofessionnel): CreationProtocolePluriprofessionnel {
    const patients: Array<CreationProtocolePluriprofessionnelPatient> = [];
    if (protocol.patients) {
      for (const p of protocol.patients) {
        patients.push({
          id: p.id,
          dateEntree: p.dateEntree ? p.dateEntree : undefined,
          dateSortie: p.dateSortie ? p.dateSortie : undefined
        });
      }
    }

    const caregivers: Array<ReunionPluriprofessionnellePersonnelSoignant> = [];
    if (protocol.personnelSoignant) {
      for (const p of protocol.personnelSoignant) {
        caregivers.push({
          id: p.id && p.id !== '' ? p.id : undefined,
          type: p.type,
          nom: p.nom
        });
      }
    }

    return {
      estValide: protocol.estValide ? true : false,
      impressionEnTeteReferent: protocol.impressionEnTeteReferent ? true : false,
      patients,
      personnelSoignant: caregivers,
      protocole: protocol.protocole ? protocol.protocole : undefined,
      referent: protocol.referent ? protocol.referent.id : undefined,
      titre: protocol.titre ? protocol.titre : undefined
    } as CreationProtocolePluriprofessionnel;
  }
}
